import { Card, CloseIcon, IconButton, Stack } from '@instabox/consumer-web-ui'
import { HTMLMotionProps, motion } from 'framer-motion'
import Image from 'next/legacy/image'
import { FC, ReactNode } from 'react'

import { ENV_CONFIG } from '../../constants/envConfig'
import { GetBannersQuery } from '../../graphql/__generated__/types'
import { Unarray } from '../../types'
import { getTranslation } from '../../utils'
import Markdown from '../Markdown'
import styles from './banner.module.scss'

export type BannerPosition = 'relative' | 'fixed-top' | 'fixed-bottom'

interface BannerProps extends Unarray<GetBannersQuery['banners']> {
  onClose?: () => void
  stagger?: number
  position?: BannerPosition
  children?: ReactNode | ReactNode[]
}

const Banner: FC<BannerProps> = ({
  dismissible = false,
  content,
  icon,
  link,
  stagger,
  translations,
  position = 'relative',
  children,
  onClose,
}) => {
  return (
    <AnimatedCard
      className={styles.card}
      size="xs"
      {...bannerAnimation}
      style={
        position === 'fixed-top'
          ? { transform: `translateY(calc(${stagger} * var(--gap-xxl)))` }
          : {}
      }>
      <Stack layout="horizontal" align="center" justify="space-between">
        <WrappedLink href={link}>
          <Stack layout="horizontal" gap="sm" align="center">
            {icon && (
              <div className={styles.image}>
                <Image
                  src={`${ENV_CONFIG.DIRECTUS_URL}/assets/${icon.id}`}
                  alt={icon.title || ''}
                  width={icon.width || 28}
                  height={icon.height || 28}
                />
              </div>
            )}
            <Markdown>{getTranslation('content', translations, content)}</Markdown>
            {children}
          </Stack>
        </WrappedLink>
        {dismissible && (
          <IconButton
            variant="light"
            icon={<CloseIcon height={24} width={24} />}
            label="Close banner"
            autoIconWidth={false}
            onClick={onClose}
          />
        )}
      </Stack>
    </AnimatedCard>
  )
}

export default Banner

interface WrappedLinkProps {
  href?: string
  children: ReactNode
}

function WrappedLink({ children, href }: WrappedLinkProps) {
  if (!href) {
    return <>{children}</>
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" className="no-underline">
      {children}
    </a>
  )
}

const AnimatedCard = motion(Card)

const bannerAnimation: HTMLMotionProps<'div'> = {
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  transition: {
    duration: 0.3,
    ease: 'easeInOut',
  },
  variants: {
    hidden: { y: '-20px', opacity: 0 },
    visible: { y: '0', opacity: 1 },
  },
}
